import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { cn } from "@/lib/utils";
import { InfoIcon } from "lucide-react";

export type FieldDescriptionTrigger = "icon" | "label";

export interface FieldLabelWithDescriptionProps {
    label: string;
    description?: string | null;
    className?: string;
    as?: "span" | "p";
    htmlFor?: string;
    descriptionTrigger?: FieldDescriptionTrigger;
}

export function FieldLabelWithDescription({ label, description, className, as = "span", htmlFor, descriptionTrigger = "icon" }: FieldLabelWithDescriptionProps) {
    const trimmedDescription = description?.trim();
    const Tag = as === "p" ? "p" : "span";

    if (!trimmedDescription) {
        if (htmlFor) {
            return (
                <label htmlFor={htmlFor} className={className}>
                    {label}
                </label>
            );
        }
        return <Tag className={className}>{label}</Tag>;
    }

    if (descriptionTrigger === "label") {
        // Keep `className` on the outermost element so grid col-span / truncate
        // from callers remain on the grid item. Wrapping with SimpleTooltip at the
        // root would nest those classes and let long labels collide with values.
        const trigger = htmlFor ? (
            <label htmlFor={htmlFor} tabIndex={0} className="block min-w-0 cursor-help truncate">
                {label}
            </label>
        ) : (
            <span tabIndex={0} className="block min-w-0 cursor-help truncate">
                {label}
            </span>
        );

        return (
            <Tag className={cn("min-w-0", className)}>
                <SimpleTooltip message={trimmedDescription} asChild contentClassName="max-w-xs whitespace-normal break-words">
                    {trigger}
                </SimpleTooltip>
            </Tag>
        );
    }

    const cue = (
        <SimpleTooltip message={trimmedDescription} asChild contentClassName="max-w-xs whitespace-normal break-words">
            <button
                type="button"
                tabIndex={0}
                className="inline-flex shrink-0 items-center justify-center rounded-sm text-gray-400 hover:text-gray-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400"
                aria-label="Field description"
                onClick={e => e.stopPropagation()}
                onPointerDown={e => e.stopPropagation()}
            >
                <InfoIcon className="size-3" />
            </button>
        </SimpleTooltip>
    );

    const content = (
        <>
            <span className="min-w-0 truncate">{label}</span>
            {cue}
        </>
    );

    if (htmlFor) {
        return (
            <label htmlFor={htmlFor} className={cn("inline-flex min-w-0 items-center gap-0.5", className)}>
                {content}
            </label>
        );
    }

    return <Tag className={cn("inline-flex min-w-0 items-center gap-0.5", className)}>{content}</Tag>;
}
